import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Loader2 } from 'lucide-react';
import Modal from 'components/ui/Modal';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';
import { entitlementsApi, usePaymentMethods } from 'modules/entitlements';
import { PaymentMethodPicker } from './PaymentMethodPicker';
import { isOnlineMethod, type PaymentMethod } from 'modules/payments/methods';
import { COIN_TOPUP_PACKS, COIN_TOPUP_MIN, COIN_TOPUP_MAX } from 'modules/coins/constants';
import { formatCoinCount, formatSom } from './billingHelpers';

interface TopUpModalProps {
  open: boolean;
  onClose: () => void;
}


const POPULAR_PACK = 50_000;

/** A choice card: the chosen one is outlined in ink (location and state), never lime. */
const OPTION_CLASS =
  'rounded-md border bg-card p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
const OPTION_ON = 'border-foreground ring-1 ring-foreground';
const OPTION_OFF = 'border-border hover:border-foreground/30';

/** The wallet is credited by the provider webhook; the browser just returns to billing. */
export function TopUpModal({ open, onClose }: TopUpModalProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<number | 'custom'>(POPULAR_PACK);
  const [customValue, setCustomValue] = useState('');
  const [chosenMethod, setMethod] = useState<PaymentMethod | null>(null);
  const methods = usePaymentMethods(open, 'coins');
  const method = methods.pick(chosenMethod);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The modal instance persists across opens (only Modal's subtree unmounts), so
  // clear any prior error/in-flight state each time it reopens.
  useEffect(() => {
    if (open) {
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const coins = useMemo(() => {
    if (selected === 'custom') {
      const n = Math.floor(Number(customValue));
      return Number.isFinite(n) && n > 0 ? n : 0;
    }
    return selected;
  }, [selected, customValue]);

  const amount = coins; // 1 coin = 1 so'm
  const valid = coins >= COIN_TOPUP_MIN && coins <= COIN_TOPUP_MAX;

  const pay = async () => {
    // Coin top-ups only list online methods; the guard narrows the type.
    if (!valid || submitting || !method || !isOnlineMethod(method)) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await entitlementsApi.CreateCoinTopup({ method, coins });
      window.location.href = data.checkoutUrl;
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('checkout_failed', 'Could not start checkout. Please try again.');
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={t('buy_coins', 'Buy coins')}
      description={t('buy_coins_subtitle', "1 coin = 1 so'm · buy coins to spend on your subscription")}
      size="md"
    >
      <div className="space-y-5">
        {/* Packs */}
        <div className="grid grid-cols-2 gap-3">
          {COIN_TOPUP_PACKS.map((pack) => {
            const on = selected === pack;
            return (
              <button
                key={pack}
                type="button"
                onClick={() => setSelected(pack)}
                aria-pressed={on}
                className={cn(OPTION_CLASS, on ? OPTION_ON : OPTION_OFF)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xl font-bold tabular-nums text-foreground">
                    {formatCoinCount(pack)}{' '}
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('coins_unit', 'coins')}
                    </span>
                  </span>
                  {pack === POPULAR_PACK && <Badge className="shrink-0">{t('popular', 'Popular')}</Badge>}
                </div>
                <div className="mt-2 text-[13.5px] tabular-nums text-muted-foreground">{formatSom(pack)}</div>
              </button>
            );
          })}

          {/* Custom */}
          <button
            type="button"
            onClick={() => setSelected('custom')}
            aria-pressed={selected === 'custom'}
            className={cn(OPTION_CLASS, 'col-span-2', selected === 'custom' ? OPTION_ON : OPTION_OFF)}
          >
            <div className="text-sm font-semibold text-foreground">{t('custom_amount', 'Custom amount')}</div>
            {selected === 'custom' ? (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  min={COIN_TOPUP_MIN}
                  max={COIN_TOPUP_MAX}
                  autoFocus
                  value={customValue}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setCustomValue(e.target.value)}
                  placeholder={t('enter_coin_amount', 'Enter coin amount')}
                />
                <span className="whitespace-nowrap text-xs text-muted-foreground">{t('coins_unit', 'coins')}</span>
              </div>
            ) : (
              <div className="mt-1 text-[13.5px] text-muted-foreground">
                {t('choose_your_own_amount', 'Choose your own amount')}
              </div>
            )}
          </button>
        </div>

        {selected === 'custom' && !valid && (
          <p className="text-xs text-muted-foreground">
            {t('topup_range_hint', {
              defaultValue: 'Enter between {{min}} and {{max}} coins.',
              min: formatCoinCount(COIN_TOPUP_MIN),
              max: formatCoinCount(COIN_TOPUP_MAX),
            })}
          </p>
        )}

        <PaymentMethodPicker
          methods={methods.methods}
          value={method}
          onChange={setMethod}
          isLoading={methods.isLoading}
        />

        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Summary + pay */}
        <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
          <div className="leading-tight">
            <div className="text-xs text-muted-foreground">{t('youll_receive', "You'll receive")}</div>
            <div className="text-base font-bold tabular-nums text-foreground">
              {t('buy_coins_summary', {
                defaultValue: '{{coins}} coins for {{amount}}',
                coins: formatCoinCount(valid ? coins : 0),
                amount: formatSom(valid ? amount : 0),
              })}
            </div>
          </div>
          <Button onClick={pay} disabled={!valid || submitting || !method} className="gap-1.5">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {!submitting && <CreditCard className="h-4 w-4" aria-hidden />}
            {t('pay_amount', { defaultValue: 'Pay {{amount}}', amount: formatSom(valid ? amount : 0) })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default TopUpModal;
